import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { attempt } from '~/api/attempt'
import { api, NeedsSignIn } from '~/api/client'
import type { CalendarBoot, CalendarEntry, LondonMonth, Viewer } from '~/api/types'

import type { ViewKey } from './views'

/**
 * The calendar's rows, above the routes.
 *
 * They used to belong to the `/calendar` route, which was fine until the event sheet had to
 * open over the front page. A sheet opened from the highlights needs the same rows — to know
 * who is going, and to patch a heart — and a route cannot lend its state to something
 * rendered beside it.
 *
 * So the rows live here and both read them. The fetch is lazy: the front page does not need
 * seven thousand rows to draw twelve cards, and asking for them on every visit to `/` would
 * put half a second on the one page that is currently instant. `load()` is called by whatever
 * needs them — the calendar on mount, the sheet when it opens.
 */
export interface CalendarData {
  /**
   * The window that is loaded: today up to `until`, or all of it when `until` is null. A
   * widening appends the nights past the old edge to the rows already held and replaces
   * none of them, so a row's identity survives a page-turn — and so a row that changed on
   * the server inside the held window reaches the browser on the next full load, not on
   * the next page-turn.
   */
  entries: CalendarEntry[]
  /**
   * What each night would send with the cap lifted — `{iso: total}`. Not what was on the
   * night: one that has gone counts only the rows somebody claimed, because the payload
   * sends nothing else from it.
   *
   * Empty until the first fetch lands, which reads as "count off the rows" everywhere it is
   * used rather than as "every night is empty". See `CalendarBoot.nights` for the ended-night
   * rule in full, and for why a night with chips on it can be missing a key.
   */
  nights: Record<string, number>
  me: Viewer | null
  today: string
  /** How much is in each tab, over the whole calendar. Empty until the first fetch lands. */
  counts: Partial<Record<ViewKey, number>>
  /** What the briefing would still ask about. Zero for anybody but the editor. */
  outstanding: number
  /** The nights the whole calendar spans, not the nights the window holds. */
  first: string | null
  last: string | null
  /** Whether the calendar continues past the loaded window. */
  more: boolean
  /** Null until something asks. Not an error: nothing has needed the rows yet. */
  loaded: boolean
  /**
   * True while the window a view has asked for with `load` reaches past what has landed. A
   * `prefetch` going out or coming back never moves it: the nights a prefetch asks for are
   * not on screen, so nothing is waiting on them.
   */
  widening: boolean
  /**
   * Whether `/entries` has landed for `night`: the window reaches it. The grids ask this of
   * the nights they draw rather than inferring it from a night having no rows, because a held
   * night with nothing on it is a fact, and a night past the edge is a question.
   */
  holds: (night: string) => boolean
  failed: string | null
  /**
   * Ask for the calendar out to `until` — a night, or null for all of it.
   *
   * Idempotent and monotonic: asking for less than is already loaded does nothing, so a
   * view can state what it needs on every render without thinking about what came before.
   */
  load: (until?: string | null) => void
  /**
   * Ask for the calendar out to `until` once what `load` asked for has landed — the window
   * the reader's next step will need, so that the step draws from rows already held.
   *
   * Monotonic like `load`, and below it: it goes out only once nothing `load` asked for is
   * still to come, so the window on screen never waits behind it. A failure is not `failed`:
   * nothing on screen was waiting, so the screen stays as it is, and the edge that failed is
   * not asked for again as a prefetch — a `load` that reaches it, when the reader steps there,
   * is what asks again.
   */
  prefetch: (until: string) => void
  /**
   * Months held whole — `/history`'s rows for each month that has arrived, keyed by the
   * month: every row on every night of it, the nights that have gone and the nights ahead,
   * claimed or not, with nobody's marks. Named when it held only the past; it is now what
   * Everything draws a night whole from, since `entries` sends a night ahead at the depth
   * the month cell draws. A second store beside `entries`, never merged into it: `entries`
   * is a prefix starting at `since`, `load` only ever widens its far edge, and a widening
   * appends the new nights to what is held rather than replacing the list — a fetch that
   * replaced it with a differently-shaped list is what reset the list view under a reader
   * on the lens branch. A row `entries` or `claimed` also holds is drawn from there, which
   * carries the viewer's marks; the rows here carry nobody's when they land, and `decide`
   * patches them as it patches `entries`, so a tick on one flips and stays flipped.
   */
  gone: Record<LondonMonth, CalendarEntry[]>
  /**
   * Ask for one month, whole. Idempotent: a month loaded or in flight is not asked again,
   * so a view can name the months it is drawing on every render. A month that failed lands
   * as empty rather than as `failed` — the calendar stands on `entries` — and is forgotten,
   * so a later ask for it goes to the server again.
   */
  wantGone: (month: LondonMonth) => void
  /**
   * The first night `entries` holds a gone row on — the server's `since` — or null when the
   * boot has not landed or names none. Read, never computed: the edge is the server's.
   */
  since: string | null
  /**
   * `/history/claimed`'s rows: everything this viewer claimed on a night before `since`,
   * with their marks. A third store beside `entries` and `gone`, and null until it has
   * arrived. A row in it is not in `entries` when both come from one `since`, but `since` can
   * move while a page is open, so `shown` dedupes the two rather than trusting it; a row in it may
   * also be in a month of `gone`, and this copy carries the viewer's marks where that one
   * carries nobody's.
   */
  claimed: CalendarEntry[] | null
  /**
   * Ask for `claimed`. Idempotent: once asked and not refused, it is not asked again for
   * this page load. A refusal lands as empty — the calendar stands on what it holds — and is
   * forgotten, so the next view that needs the body asks again.
   */
  wantClaimed: () => void
  save: (id: string, on: boolean) => void
  going: (id: string, on: boolean) => void
  /**
   * Follow an act. Returns whether the write took, unlike `save` and `going`, so a caller
   * with state of its own can wait for it and put its own mark back — the event sheet holds
   * `detail.favourites` separately from the calendar's rows, and patching one leaves the
   * other saying the opposite.
   */
  star: (artistId: number, on: boolean) => Promise<boolean>
}

/** One press's patch: a row it is about comes back changed, any other as the same object. */
type Change = (e: CalendarEntry) => CalendarEntry

/** A press drawn over the stores until nothing is out and it can be settled into them. */
interface Press {
  change: Change
  /** Whether its write has had an answer, either way. A refused press leaves the list. */
  answered: boolean
  /** The tick of `clock` its answer came at, once it has one. */
  answeredAt: number
}

/** An accepted press written into the stores, kept while a request sent before its answer is out. */
interface Settled {
  change: Change
  answeredAt: number
}

/**
 * Whether far edge `a` reaches past far edge `b`. `null` is the whole calendar and reaches
 * past every night; `undefined` is nothing held, which everything reaches past.
 */
function beyond(a: string | null, b: string | null | undefined): boolean {
  if (b === undefined) return true
  if (b === null) return false
  return a === null || a > b
}

/**
 * The press list when there is none: one shared object, so two empty lists are never told
 * apart by identity.
 */
const NONE: readonly Change[] = []

/**
 * One store's last replay: what went in, the changes array it went in with, and what came
 * out — the latest only. Asked again for the same input and the same changes array, it hands
 * back the replay it is holding rather than making another, so a render that draws a store
 * twice, and `settle` writing the drawn store back, all end up holding the one object.
 *
 * The latest only, and one per store, because anything wider keeps every generation: a
 * replay's output becomes the store, so the store becomes the next replay's input, and a
 * table keyed by input holds each output as a value beside the key it will be. Once any old
 * store is still reachable — a render's closure holds the stores it drew — the whole run of
 * stores after it is too. Here an older replay is dropped the moment a newer one is made.
 */
interface Memo<T> {
  input: T | null
  changes: readonly Change[]
  output: T | null
}

const memo = <T,>(): Memo<T> => ({ input: null, changes: NONE, output: null })

/** A memo for the record of months, and one for each month's list inside it. */
interface MonthsMemo {
  whole: Memo<Record<LondonMonth, CalendarEntry[]>>
  each: Map<LondonMonth, Memo<CalendarEntry[]>>
}

function remembered<T extends object>(
  last: Memo<T>,
  input: T,
  changes: readonly Change[],
  make: () => T,
): T {
  if (last.input === input && last.changes === changes && last.output) return last.output
  const output = make()
  last.input = input
  last.changes = changes
  last.output = output
  return output
}

/**
 * `list` with each change applied to every row, in order — the list itself when there are
 * none. A row no change is about stays the same object, so a chip nobody pressed is not
 * handed a new one.
 */
function replay(
  last: Memo<CalendarEntry[]>,
  list: CalendarEntry[],
  changes: readonly Change[],
): CalendarEntry[] {
  if (!changes.length) return list
  return remembered(last, list, changes, () =>
    list.map(e => changes.reduce((row, change) => change(row), e)),
  )
}

/** `replay` over every month held whole, keyed as it was. */
function overMonths(
  last: MonthsMemo,
  months: Record<LondonMonth, CalendarEntry[]>,
  changes: readonly Change[],
): Record<LondonMonth, CalendarEntry[]> {
  if (!changes.length) return months
  return remembered(last.whole, months, changes, () =>
    Object.fromEntries(
      Object.entries(months).map(([month, list]) => {
        let one = last.each.get(month)
        if (!one) last.each.set(month, (one = memo()))
        return [month, replay(one, list, changes)]
      }),
    ),
  )
}

/*
 * The three patches, built here rather than inline in the provider so that each closes over
 * its own arguments and nothing else. One made inline closes over the render it was pressed
 * from, and that render's `over` holds the press before it — which, settled only at the next
 * press, is still there — so every press would keep the one before it, and its render's
 * stores, for as long as the last one is held.
 */

/**
 * The heart. `picked` moves with it for the editor, because the server moves it: their
 * heart writes `curation_status`, so their own list *is* the published one.
 *
 * Patching `liked` alone made the mark one-way. The heart they see is `picked || liked`, so
 * un-hearting cleared the half the patch touched, left `picked` true, and drew a filled
 * heart — a control that could be turned on and never off, and gave no sign which of the two
 * it had failed at.
 */
const hearted =
  (id: string, on: boolean, editor: boolean): Change =>
  e =>
    e.id === id ? { ...e, liked: on, picked: editor ? on : e.picked } : e

/** The tick. Saying you are going puts it on the list; unsaying it does not take it off. */
const ticked =
  (id: string, on: boolean): Change =>
  e =>
    e.id === id ? { ...e, going: on, liked: on || e.liked } : e

/**
 * Following an act adopts every gig they headline, so the rows we hold are patched where the
 * act is the headliner — the same rule the server applies. Anything subtler would be the
 * client guessing at a ranking it does not compute.
 */
const followed =
  (artistId: number, on: boolean): Change =>
  e =>
    e.act?.id === artistId ? { ...e, starred: on, picked: on || e.picked } : e

const ROWS = createContext<CalendarData | undefined>(undefined)

export function CalendarProvider({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<CalendarBoot | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [gone, setGone] = useState<Record<LondonMonth, CalendarEntry[]>>({})
  const [claimed, setClaimed] = useState<CalendarEntry[] | null>(null)

  /*
   * The presses drawn over the three stores, in the order they were made, and the same list
   * as state for render to draw from. The ref is the list: an answer has to know at once
   * whether any other write is still out, which state read from a closure cannot tell it.
   * `drawn` is the changes array last handed to `over`, kept so `settle` asks `replay` for
   * the very array render drew with. Both are only ever set together, by `draw`.
   *
   * Declared above the requests, because every request's answer settles the presses before
   * it writes a store.
   */
  const out = useRef<Press[]>([])
  const drawn = useRef<readonly Change[]>(NONE)
  /** Each store's last replay — see `Memo`. Shared by the drawing below and by `settle`. */
  const [memos] = useState(() => ({
    entries: memo<CalendarEntry[]>(),
    claimed: memo<CalendarEntry[]>(),
    months: { whole: memo(), each: new Map() } as MonthsMemo,
  }))
  const [over, setOver] = useState<readonly Change[]>(NONE)

  const draw = useCallback(() => {
    drawn.current = out.current.length ? out.current.map(p => p.change) : NONE
    setOver(drawn.current)
  }, [])

  /*
   * One count for the moments a request is sent and a press is answered, so that a store
   * landing can tell which presses the server had already taken when it was asked. A press
   * answered before a request went out is in what that request brings; one answered after —
   * or pressed after — may not be, since the server can have read the row before it wrote
   * the press.
   */
  const clock = useRef(0)
  /** The tick each request still out was sent at. */
  const flying = useRef(new Set<number>())
  /**
   * Accepted presses already written into the stores, with the tick of their answer, kept
   * only while a request sent before that answer is still out. `settle` empties `out`, so
   * without this a press settled while a request was out would be gone from everything by
   * the time that request's rows landed, and they would be drawn as sent — unmarked.
   */
  const kept = useRef<Settled[]>([])

  const sent = useCallback(() => {
    const at = ++clock.current
    flying.current.add(at)
    return at
  }, [])

  /**
   * The rows a request sent at `at` brought, with every accepted press it is owed written
   * in: each one settled since, in the order it was made. A press still in `out` is not
   * here — `over` draws it, and `settle` writes it into this store with the rest.
   */
  const owed = useCallback((at: number, list: CalendarEntry[]): CalendarEntry[] => {
    const changes = kept.current.filter(p => p.answeredAt > at).map(p => p.change)
    if (!changes.length) return list
    return list.map(e => changes.reduce((row, change) => change(row), e))
  }, [])

  /** A request sent at `at` has landed or failed: forget it, and what only it was owed. */
  const finish = useCallback((at: number) => {
    flying.current.delete(at)
    // `Math.min()` of nothing is Infinity, so with nothing out every kept press goes.
    const oldest = Math.min(...flying.current)
    kept.current = kept.current.filter(p => p.answeredAt > oldest)
  }, [])

  /**
   * Write the presses into the stores once every one has been answered, and stop drawing
   * them over. Called before anything else writes a store — a press, the boot or a widening
   * landing, a month, the claimed body — and never on an answer, so an accepted press sets
   * no state here: the screen already shows it, and nothing has to change until something
   * else does. Settling first is what keeps a store that was asked for after the last answer
   * as the server sent it, with no press replayed over it, as it would be had the presses
   * been written in the moment that answer came. A store asked for *before* an answer is
   * given that press by `owed` instead, which is why each settled press is kept while such a
   * request is out.
   *
   * Every updater replays the stores with `drawn`, the array render last drew with, through
   * the same memo render drew through, and a memo hands back what it made from the same input
   * and the same array — so a store that has not moved since that render becomes exactly the
   * object already on screen, and the views memoised on it see nothing change. A store that
   * has moved in between is replayed afresh and is right, merely new.
   */
  const settle = useCallback(() => {
    if (!out.current.length || out.current.some(p => !p.answered)) return
    // `drawn` is `out`'s changes here: every write to `out` but an acceptance is followed by
    // `draw`, and an acceptance marks a press answered without moving the list.
    const settled = drawn.current
    if (flying.current.size)
      kept.current.push(...out.current.map(p => ({ change: p.change, answeredAt: p.answeredAt })))
    out.current = []
    // Patched rather than refetched: the calendar is every row in the window and a heart is
    // one of them. The briefing refetches, for the opposite reason — its pile is a
    // server-side window and the browser only ever had its first page.
    setBoot(was => (was ? { ...was, entries: replay(memos.entries, was.entries, settled) } : was))
    // And the past, every month of it. A history row is drawn by the same chip and pressed by
    // the same control; patching `entries` alone left its tick unlit after the server had
    // said yes, which is the mark the venue page's `touch` had to learn to keep.
    setGone(was => overMonths(memos.months, was, settled))
    // And the claimed body, which is where a gone night before `since` draws its marked rows
    // from: a tick pressed there and patched elsewhere would draw unlit.
    setClaimed(was => (was ? replay(memos.claimed, was, settled) : was))
    draw()
  }, [draw, memos])

  /**
   * The far edge `load` has asked for: a night, `null` for the whole calendar, and
   * `undefined` for "nobody has asked yet". State for render (`widening`), and the same
   * again as a ref for the request chain, which has to read it the moment an answer lands.
   *
   * `null` is the widest thing there is, so once anything asks for it no narrower request
   * can undo it — which is what makes `load` safe to call from render.
   */
  const [wanted, setWanted] = useState<string | null | undefined>(undefined)
  const want = useRef<string | null | undefined>(undefined)
  /** The far edge that is actually loaded, so a widening knows whether it has landed. */
  const [held, setHeld] = useState<string | null | undefined>(undefined)
  /** `held` again, as a ref, for the chain to build the next request from. */
  const landed = useRef<string | null | undefined>(undefined)
  /** The far edge `prefetch` has asked for, past `want`; cleared when one fails. */
  const ahead = useRef<string | undefined>(undefined)
  /** The far edge of the last prefetch that failed, which is not asked for again as one. */
  const refused = useRef<string | undefined>(undefined)
  /** Whether an `/entries` request is out. One at a time: see `pump`. */
  const asking = useRef(false)

  /**
   * Send the next `/entries` request, if one is due and none is out.
   *
   * One at a time, each from the edge the one before it landed at. A request is never
   * cancelled for a wider one: the wider one is sent when this one lands, from its edge, so
   * no night is asked for twice and no answer is thrown away — cancelling and restarting from
   * the old edge sent the abandoned nights again, made the step wait for a request twice the
   * size, and left the abandoned one on the server's lock anyway.
   *
   * What `load` asked for goes first; what `prefetch` asked for goes only once that has
   * landed, so the window on screen never waits behind the next one.
   *
   * A widening asks for the nights past the edge already held and appends them: the
   * server's `entries(to=A)` and `entries(to=U, after=A)` hold every row of `entries(to=U)`
   * once each — the same rows as a whole request, though not always in the same order
   * across a night boundary, which the views do not need since they group by night — with
   * the rows already held kept as the same objects, which is what keeps a heart drawn on one
   * from remounting, and what lets `decide` settle a press into the one list it always did.
   * `after` is a night only: `undefined` is the first fetch, which has nothing to append to,
   * and `null` is the whole calendar, past which nothing lies.
   */
  const pump = useCallback(() => {
    if (asking.current || want.current === undefined) return
    const edge = landed.current
    const to = beyond(want.current, edge) ? want.current : ahead.current
    if (to === undefined || !beyond(to, edge)) return
    asking.current = true
    const after = typeof edge === 'string' ? edge : undefined
    const at = sent()
    api
      .entries(to ?? undefined, after)
      .then(data => {
        settle()
        // The first body is kept exactly as sent; only an appended one is given the presses
        // it is owed. The provider's press tests pin the first `/entries` as the server's
        // word even when a press was answered while it was out, which the rule `owed`
        // applies to every other store would not.
        const brought = after === undefined ? data.entries : owed(at, data.entries)
        finish(at)
        setBoot(was =>
          was && after !== undefined ? { ...data, entries: was.entries.concat(brought) } : data,
        )
        landed.current = to
        setHeld(to)
        asking.current = false
        pump()
      })
      .catch((error: unknown) => {
        finish(at)
        asking.current = false
        // Not a failure the reader caused: the calendar is public, so this only happens if
        // the rules change under them.
        if (error instanceof NeedsSignIn) {
          window.location.href = `/login?next=${error.next}`
          return
        }
        // Something on screen was waiting on these nights, so the calendar says it could not
        // be reached. `want` only grows, so a request that was for the screen when it was
        // sent is for the screen still.
        if (want.current !== undefined && beyond(want.current, landed.current)) {
          setFailed(error instanceof Error ? error.message : String(error))
          return
        }
        // A prefetch: nothing on screen was waiting. Its edge is not asked for again as a
        // prefetch — a view states its prefetch on every render, and a server that is
        // refusing would be asked again on each — and the step into those nights asks with
        // `load`, from the same edge this one was sent from.
        if (typeof to === 'string') refused.current = to
        ahead.current = undefined
      })
  }, [sent, settle, owed, finish])

  const load = useCallback(
    (until: string | null = null) => {
      // Monotonic. `null` is everything and beats every night; otherwise the later night
      // wins. A view asking for August while the list is showing December must not throw
      // December's rows away.
      if (want.current !== undefined && !beyond(until, want.current)) return
      want.current = until
      setWanted(until)
      pump()
    },
    [pump],
  )

  const prefetch = useCallback(
    (until: string) => {
      if (refused.current !== undefined && !beyond(until, refused.current)) return
      if (ahead.current !== undefined && !beyond(until, ahead.current)) return
      ahead.current = until
      pump()
    },
    [pump],
  )

  /**
   * The months asked for, as a ref rather than state: the guard has to be synchronous. Two
   * renders in one tick both call `wantGone` before either's state update lands, and a guard
   * on state alone would send the month twice. What a reader needs to know — whether a month
   * has arrived — is `gone` itself, which is state.
   */
  const asked = useRef(new Set<LondonMonth>())

  const wantGone = useCallback(
    (month: LondonMonth) => {
      if (asked.current.has(month)) return
      asked.current.add(month)
      const at = sent()
      api
        .history(month)
        .then(data => {
          settle()
          const brought = owed(at, (data.entries ?? []) as CalendarEntry[])
          finish(at)
          setGone(was => ({ ...was, [month]: brought }))
        })
        .catch(() => {
          // Not `failed`: that takes the whole calendar down to a sentence, and a month of the
          // past not arriving leaves everything `entries` holds still standing. The month
          // lands empty, so whatever was waiting on it — the list's sentinel — moves on rather
          // than waiting for ever; and it is forgotten, so the next ask for it (a change of
          // month, a step back to it) goes to the server again rather than reading the blank.
          settle()
          finish(at)
          setGone(was => (month in was ? was : { ...was, [month]: [] }))
          asked.current.delete(month)
        })
    },
    [sent, settle, owed, finish],
  )

  /** Whether `claimed` has been asked for, as a ref for the reason `asked` above is one. */
  const askedClaimed = useRef(false)

  const wantClaimed = useCallback(() => {
    if (askedClaimed.current) return
    askedClaimed.current = true
    const at = sent()
    api
      .claimed()
      .then(data => {
        settle()
        const brought = owed(at, data.entries ?? [])
        finish(at)
        setClaimed(brought)
      })
      .catch(() => {
        // As a month of `gone` fails: empty rather than `failed`, so the calendar stays up
        // and nothing waits on it for ever, and forgotten, so a later view asks again.
        settle()
        finish(at)
        setClaimed(was => was ?? [])
        askedClaimed.current = false
      })
  }, [sent, settle, owed, finish])

  const decide = useCallback(
    async (write: () => Promise<unknown>, change: Change) => {
      /*
       * Draw first, write second, and take the press back if the write is refused.
       *
       * It was the other way round — write, then patch on a yes — so that a refused press
       * left the row where it was and said so. It still says so: `attempt` puts the refusal
       * on screen. What the old order cost was the whole round trip before the heart moved,
       * which on a phone reads as the control not working, and is what was reported.
       *
       * The stores hold what the server delivered plus the presses already settled; a press
       * lives in `out` from the moment it is made until it is settled, and what a view reads
       * is every store with that list replayed over it, in order — the briefing's
       * page-plus-presses, fitted to three stores instead of one page. A refusal drops its
       * own press from the list and the stores are drawn again from what they hold, so a
       * press made on the same row while it was out stays drawn: a heart out, a tick the
       * server took, the heart refused, and the tick stays lit. Putting back a copy of the
       * row taken at press time erased that tick; negating the pressed field is no better,
       * because the patches touch more than one field — going sets `liked`, a star sets
       * `picked`, the editor's heart moves `picked` — and the refused heart's `liked` is the
       * tick's to keep.
       *
       * Nothing is copied, so nothing stale can come back: a month, the claimed body or a
       * widening that lands while a press is out is drawn with the presses over it like any
       * other row, and a refusal leaves it as the server sent it.
       *
       * Settled into the stores only once nothing is out, so the order the list keeps is
       * never broken — an accepted press settled early would land beneath one made before it.
       * Each settle is an updater over whatever the store holds when it runs, so a store that
       * landed while the press was out gets the accepted press too, which is what it was
       * drawn with.
       */
      settle()
      const made: Press = { change, answered: false, answeredAt: 0 }
      out.current = [...out.current, made]
      draw()
      const took = await attempt(write)
      made.answered = true
      made.answeredAt = ++clock.current
      // Accepted: the press is drawn already and stays in the list until `settle`, so nothing
      // on screen moves and nothing is set.
      if (took) return true
      // Refused, or a sign-in. The sentence on screen is `attempt`'s.
      out.current = out.current.filter(p => p !== made)
      draw()
      return false
    },
    [settle, draw],
  )

  // One function per edge, so a view memoised on it moves when the edge does and not before.
  const holds = useCallback(
    (night: string) => held === null || (held !== undefined && night <= held),
    [held],
  )

  // What a view reads: each store with the unsettled presses drawn over it. With none, the
  // store itself, so a view memoised on it sees the same object until it really moves.
  const entries = useMemo(
    () => replay(memos.entries, boot?.entries ?? [], over),
    [memos, boot, over],
  )
  const goneDrawn = useMemo(() => overMonths(memos.months, gone, over), [memos, gone, over])
  const claimedDrawn = useMemo(
    () => (claimed ? replay(memos.claimed, claimed, over) : claimed),
    [memos, claimed, over],
  )

  const value: CalendarData = {
    entries,
    nights: boot?.nights ?? {},
    me: boot?.me ?? null,
    today: boot?.today ?? '',
    counts: boot?.counts ?? {},
    outstanding: boot?.outstanding ?? 0,
    first: boot?.first ?? null,
    last: boot?.last ?? null,
    // The window stops short of the calendar's own last night. `held === null` is the
    // whole thing, which never has more.
    more: boot !== null && held !== null && held !== undefined && !!boot.last && held < boot.last,
    loaded: boot !== null,
    // Past what has landed, not merely different from it: a prefetch that has landed leaves
    // `held` beyond `wanted`, and nothing is widening then.
    widening: boot !== null && wanted !== undefined && beyond(wanted, held),
    holds,
    failed,
    load,
    prefetch,
    gone: goneDrawn,
    wantGone,
    since: boot?.since ?? null,
    claimed: claimedDrawn,
    wantClaimed,
    save: (id, on) =>
      void decide(() => api.save(id, on), hearted(id, on, !!boot?.me?.isEditor)),
    going: (id, on) => void decide(() => api.going(id, on), ticked(id, on)),
    star: (artistId, on) => decide(() => api.star(artistId, on), followed(artistId, on)),
  }

  return <ROWS.Provider value={value}>{children}</ROWS.Provider>
}

export function useCalendar(): CalendarData {
  const found = useContext(ROWS)
  if (!found) throw new Error('useCalendar outside a CalendarProvider')
  return found
}
